import { useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '@/services/api';
import { argbIntToHex } from '@/utils/color';
import { Modal, ModalHeader, ModalBody } from '@/components/ui/Modal';
import type { TableInfo, TableStatusOption } from '@/types/api';

interface TableStatusPickerProps {
  restaurantId: number;
  table: TableInfo;
  onClose: () => void;
}

export function TableStatusPicker({ restaurantId, table, onClose }: TableStatusPickerProps) {
  const {
    data: options = [],
    isLoading,
  } = useQuery({
    queryKey: ['table-statuses', restaurantId],
    queryFn: () => apiClient.getTableStatuses(restaurantId),
    enabled: !!restaurantId,
  });

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.priority - b.priority),
    [options]
  );

  const mutation = useMutation({
    mutationFn: (posStatusId: string) =>
      apiClient.setTableStatus(restaurantId, table.id, posStatusId),
    onSuccess: () => {
      // The authoritative update arrives via the WebSocket -> invalidate refresh
      // in TablesTab, so we just close here without patching the cache.
      onClose();
    },
  });

  const handleSelect = (option: TableStatusOption) => {
    if (mutation.isPending) return;
    mutation.mutate(option.posStatusId);
  };

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>
        Change status — Table {table.tableNumber}
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading statuses…</div>
        ) : sortedOptions.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No statuses synced from POS yet.
          </div>
        ) : (
          <ul className="space-y-1" role="listbox" aria-label="Table statuses">
            {sortedOptions.map((option) => {
              const swatch = argbIntToHex(option.colorRgb);
              const isCurrent =
                !!table.statusName && table.statusName === option.name;

              return (
                <li key={option.posStatusId}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    disabled={mutation.isPending}
                    onClick={() => handleSelect(option)}
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      isCurrent
                        ? 'border-primary-500 bg-primary-50 font-semibold'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300"
                      style={{ backgroundColor: swatch ?? '#e5e7eb' }}
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate text-gray-900">{option.name}</span>
                    {isCurrent && (
                      <span className="text-xs text-primary-600">Current</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {mutation.isPending && (
          <div className="mt-3 text-center text-sm text-gray-500">Updating…</div>
        )}
        {mutation.isError && (
          <div className="mt-3 text-center text-sm text-red-600">
            {axios.isAxiosError(mutation.error) &&
            mutation.error.response?.status === 400
              ? "That status isn't available — refresh and try another."
              : "Couldn't update — try again"}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

export default TableStatusPicker;
